<template>
    <div>
        <div class="exclusive-featured-events-wrapper"
            v-if="showExclusiveFeaturedEvents">
            <div class="exclusive-featured-events-heading">
                <span class="exclusive-featured-events-heading__title">{{ $config.exclusiveTitleCopy }}</span>
                <span class="exclusive-featured-events-heading__redeem">{{ $config.exclusiveRedeemCopy }}</span>
                <img
                    v-if="logoDesktop"
                    :alt="logoDesktop.alt"
                    :src="logoDesktop.url"
                    :style="logoDesktop.overrideStyles"
                    class="exclusive-featured-events-heading__logo"
                >
            </div>
            <div class="exclusive-featured-events-list-wrapper">
                <ul class="exclusive-featured-events-list"
                    v-if="$config.hasExclusiveFeaturedEventsView">
                    <li
                        class="exclusive-featured-event-card"
                        v-for="(event, index) in sortedExclusiveFeaturedEvents"
                        :key="event.id">
                        <ExclusiveFeaturedEventTile :event="event" :index="index" />
                    </li>
                </ul>
            </div>
            <router-link :to="{ name: 'exclusive' }">
                <h4 class="exclusive-featured-link" v-if="showExclusiveEventsLink">{{ $config.exclusiveLinkCopy }}</h4>
            </router-link>
        </div>
        <div class="featured-events-wrapper" v-else>
            <ul class="featured-events-list">
                <li
                    class="featured-event-card"
                    v-for="(event, index) in featuredEvents"
                    :key="event.id">
                    <router-link
                        :to="makeEventRoute(event)">
                        <div
                            class="featured-event-image featured-event-image--mobile"
                            data-e2e="home.featured-event-listing-image"
                            v-if="isWindowSmall || isWindowMedium"
                            :style="{ backgroundImage: `url(${event.imageUrlRectangle})` }"></div>
                        <div
                            class="featured-event-image featured-event-image--desktop"
                            data-e2e="home.featured-event-listing-image"
                            v-else
                            :style="{ backgroundImage: `url(${index === 0 ? event.imageUrlRectangle : event.imageUrlSquare || event.imageUrlRectangle})` }"></div>

                        <div class="featured-event-info">
                            <div class="featured-event-info__date-time">
                                <span>{{ event.name }}</span>
                            </div>
                            <div class="featured-event-info__venue-price">
                                <span v-if="event.isDateTbd">{{ $t('date-tbd') }}</span>
                                <span v-else-if="event.isTimeTbd">{{ event.localDate | date('ddd, MMM D') }}, {{ $t('time-tbd') }}</span>
                                <span v-else-if="event.localDateLabel">{{ event.localDateLabel }} at {{ event.localDate | date('h:mma') }}</span>
                                <span v-else>{{ event.localDate | date('ddd, MMM D') }} at {{ event.localDate | date('h:mma') }}</span>
                                <br>
                                <div>
                                    <span class="featured-event-info__venue-name">{{ event.venueName }} </span>
                                    <template v-if="!$config.isIntegrated">
                                        <span v-if="isLoyaltyPricing && event.pointsPriceMin">from {{ formatPoints(event.pointsPriceMin, event.pointsCurrency) }}</span>
                                        <span v-else>from {{ event.moneyPriceMin | numeral('$0,0')}}</span>
                                    </template>
                                </div>
                            </div>
                        </div>
                    </router-link>
                </li>
            </ul>
        </div>
    </div>
</template>

<script>
import eventListable from '../mixins/EventListable'
import ExclusiveFeaturedEventTile from '../components/ExclusiveFeaturedEventTile.vue'
import loyaltyFormattable from '../mixins/LoyaltyFormattable'
import resizable from '../mixins/Resizable'

export default {
    name: 'FeaturedEvents',
    components: {
        ExclusiveFeaturedEventTile
    },
    computed: {
        logoDesktop () {
            return {
                alt: this.$config.logos.desktop.alt || '',
                overrideStyles: this.$config.logos.desktop.overrideStyles || {},
                url: (this.$store.state.program || {}).logo_url || `${this.$store.state.imagesCdn}/${this.$config.logos.desktop.staticImagePath}`
            }
        },
        isLoyaltyPricing () {
            return this.$store.state.isRedeemingLoyaltyPoints
        },
        showExclusiveEventsLink () {
            return this.featuredEvents.length > 6
        },
        showExclusiveFeaturedEvents () {
            return this.$config.hasExclusiveFeaturedEventsView && this.featuredEvents.length >= 2
        },
        sortedExclusiveFeaturedEvents () {
            if (this.featuredEvents.length > 6) {
                return this.featuredEvents.slice(0, 6)
            } else {
                return this.featuredEvents
            }
        }
    },
    props: {
        featuredEvents: {
            default: null,
            required: true,
            type: Array
        }
    },
    mixins: [
        eventListable,
        loyaltyFormattable,
        resizable
    ]
}
</script>

<style lang="postcss" scoped>
@import 'theme';

.exclusive-featured-events-wrapper {
    margin-bottom: 24px;
    margin-left: -16px;
    width: 100vw;
    @media (--mq-large-min) {
        border-radius: 4px;
        margin-left: 0;
        width: auto;
    }

    & .exclusive-featured-events-heading {
        align-items: center;
        background: #1C2B39;
        color: white;
        display: flex;
        flex-direction: column;
        font-size: var(--fs-large);
        font-weight: 200;
        padding: 10px 12px 7px 12px;
        @media (--mq-large-min) {
            border-top-left-radius: 4px;
            border-top-right-radius: 4px;
            border: 1px solid #1C2B39;
            flex-direction: row;
            padding: 18px 18px 18px 24px;
        }

        & .exclusive-featured-events-heading__title {
            @media (--mq-large-min) {
                padding: 6px 24px 6px 0;
                border-right: 1px solid #FFFFFF;
            }
        }

        & .exclusive-featured-events-heading__redeem {
            font-size: var(--fs-small);
            @media (--mq-large-min) {
                padding: 0 0 0 24px;
                font-size: inherit;
            }
        }

        & .exclusive-featured-events-heading__logo {
            display: none;
            @media (--mq-large-min) {
                display: flex;
                margin-left: auto;
            }
        }
    }

    & .exclusive-featured-events-list-wrapper {
        overflow: auto;
        width: 100vw;
        @media (--mq-large-min) {
            overflow: hidden;
            width: auto;
        }

        & .exclusive-featured-events-list {
            background: #FFFFFF;
            display: flex;
            flex-wrap: nowrap;
            list-style: none;
            overflow: auto;
            padding: 8px;
            width: max-content;
            @media (--mq-large-min) {
                border-bottom-left-radius: 4px;
                border-bottom-right-radius: 4px;
                border-bottom: 1px solid #979797;
                border-left: 1px solid #979797;
                border-right: 1px solid #979797;
                flex-wrap: wrap;
                padding: 0 0 24px 24px;
                width: auto;
            }

            & .exclusive-featured-event-card {
                border-radius: 4px;
                box-shadow: 0 0 4px rgba(0, 0, 0, 0.2);
                box-sizing: border-box;
                flex: 1 1 33.3%;
                height: auto;
                margin: 8px;
                max-width: 100%;
                width: 315px;
                &:first-child {
                    margin-left: 8px;
                }
                &:hover {
                    box-shadow: 0 0 8px rgba(0, 0, 0, 0.5);
                }

                @media (--mq-large-min) {
                    height: 253px;
                    margin: 24px 20px 0 0;
                    max-width: 279px;
                    width: auto;

                    &:first-child {
                        margin-left: 0;
                    }
                }
            }
        }
    }

    & .exclusive-featured-link {
        background: white;
        color: var(--c-primary);
        display: flex;
        font-size: var(--fs-large);
        font-weight: var(--fw-light);
        justify-content: center;
        margin-top: -2px;
        padding: 8px 0 16px;
        @media (--mq-large-min) {
            border-bottom-left-radius: 4px;
            border-bottom-right-radius: 4px;
            border-bottom: 1px solid #979797;
            border-left: 1px solid #979797;
            border-right: 1px solid #979797;
            padding-bottom: 24px;
        }
    }
}

.featured-events-wrapper {
    & .featured-events-list {
        display: flex;
        flex-wrap: wrap;
        list-style: none;
        margin: 5px -10px 0;
        padding: 8px 16px 24px;
        @media (--mq-large-min) {
            margin: 0 -10px 14px;
            padding: 0;
        }

        & .featured-event-card {
            box-sizing: border-box;
            flex-basis: 100%;
            height: 200px;
            max-width: 100%;
            padding: 10px;
            @media (--mq-large-min) {
                flex-basis: 33.3%;
                height: 270px;
                max-width: 33.3%;
            }

            &:first-child {
                flex-basis: 100%;
                max-width: 100%;
                @media (--mq-large-min) {
                    flex-basis: 66.6%;
                    max-width: 66.6%;
                }
            }

            & a {
                background-color: #000;
                background-position: 50% 50%;
                background-repeat: no-repeat;
                background-size: cover;
                border-radius: 5px;
                box-shadow: 0 2px 7px 0 rgba(0,0,0,0.5);
                color: white;
                display: block;
                height: 100%;
                overflow: hidden;
                position: relative;
                text-decoration: none;
                transition: 0.2s ease;

                &:focus,
                &:hover {
                    box-shadow: 0 2px 22px 0 rgba(0,0,0,0.8);
                    outline: none;
                    transition: 0.2s ease;
                }
            }

            & .featured-event-image {
                background-position: 50% 0;
                background-repeat: no-repeat;
                background-size: cover;
                bottom: 0;
                height: 100%;
                left: 0;
                position: absolute;
                right: 0;
                top: 0;
                width: 100%;
                z-index: 1;

                &.featured-event-image--desktop {
                    display: none;
                    @media (--mq-large-min) {
                        display: block;
                    }
                }

                &.featured-event-image--mobile {
                    display: block;
                    @media (--mq-large-min) {
                        display: none;
                    }
                }
            }

            & .featured-event-info {
                background: linear-gradient(0deg, rgba(0,0,0,.7) 0%, rgba(0,0,0,0) 51.13%, rgba(0, 0, 0, .7) 100%);
                bottom: 0;
                display: flex;
                flex-direction: column;
                height: 100%;
                justify-content: space-between;
                left: 0;
                padding: 17px;
                position: absolute;
                right: 0;
                text-shadow: 0 2px #000;
                top: 0;
                z-index: 10;

                & .featured-event-info__date-time {
                    font-size: var(--fs-largest);
                    font-weight: var(--fw-normal);
                }
                & .featured-event-info__venue-price {
                    font-size: var(--fs-large);
                    font-weight: var(--fw-light);
                    & div {
                        & .featured-event-info__venue-name {
                            display: none;
                            @media (--mq-medium-min) {
                                display: inline-flex
                            }
                        }
                        & span:last-child {
                            font-weight: var(--fw-medium);
                        }
                    }
                }
            }
        }
    }
}
</style>
